import {
  apiClientVersioned,
  returnData,
  type IPaginatedResponse,
} from '../index'
import {
  ANNOUNCEMENT_CONTENT_DEFAULTS,
  contentToWire,
  publishWindowHours,
  wireToActiveAnnouncement,
  wireToAnnouncement,
} from './announcementMapping'

import type {
  IActiveAnnouncement,
  IAnnouncement,
  IAnnouncementContent,
  IAnnouncementCreateWire,
  IAnnouncementDismissResponseWire,
  IAnnouncementUpdate,
  IAnnouncementUpdateWire,
  IAnnouncementWire,
  IUserAnnouncementWire,
} from '../types/announcements'

const adminPath = (id?: string) =>
  id ? `/admin/announcements/${encodeURIComponent(id)}` : '/admin/announcements'

const nowInSeconds = () => Math.floor(Date.now() / 1000)

/** Rows per admin-list request. The list is paged through in full — see `list`. */
const ADMIN_LIST_PAGE_SIZE = 200

/**
 * Admin-managed broadcast announcements (EVGPT-1725).
 *
 * Everything crossing this boundary is translated by `announcementMapping` —
 * the wire model and the model the UI reads are not the same shape. Callers
 * only ever see the domain model.
 */
export const AnnouncementsApi = {
  // #region Admin
  /**
   * Every announcement, paged through to the end.
   *
   * Nothing archives on publish, so the list only ever grows — and the admin
   * list has no paging of its own, so stopping after one page would silently hide
   * the oldest rows with nothing on screen to say so. The empty-page break is a
   * belt-and-braces stop in case the server ever misreports `pages`.
   */
  list: async (): Promise<IAnnouncement[]> => {
    const rows: IAnnouncementWire[] = []
    let page = 1
    let pages: number

    do {
      const response = await returnData(
        apiClientVersioned.get<IPaginatedResponse<IAnnouncementWire>>(
          adminPath(),
          { params: { page, size: ADMIN_LIST_PAGE_SIZE } },
        ),
      )
      const items = response.items ?? []
      if (!items.length) break

      rows.push(...items)
      pages = response.pages ?? 1
      page += 1
    } while (page <= pages)

    return rows.map(wireToAnnouncement)
  },

  /**
   * Creation takes a name only, but the wire requires `repeat` and
   * `display_duration`, so the defaults are sent in full rather than left to
   * the server — which has no per-field defaults to fall back on.
   */
  create: async (name: string): Promise<IAnnouncement> => {
    const body: IAnnouncementCreateWire = {
      title: name,
      is_active: false,
      ...contentToWire(ANNOUNCEMENT_CONTENT_DEFAULTS),
    }
    return wireToAnnouncement(
      await returnData(
        apiClientVersioned.post<IAnnouncementWire>(adminPath(), body),
      ),
    )
  },

  update: async (
    id: string,
    { name, content, isActive, displayDurationHours }: IAnnouncementUpdate,
  ): Promise<IAnnouncement> => {
    // `extra="forbid"` server-side, so only the keys actually being changed may
    // travel — an explicit `undefined` would serialise away, but a `null` from
    // a missing branch would 422.
    const body: IAnnouncementUpdateWire = {
      ...(name === undefined ? {} : { title: name }),
      ...(content === undefined ? {} : contentToWire(content)),
      ...(isActive === undefined ? {} : { is_active: isActive }),
      // Last, so it wins over the duration `contentToWire` mirrors.
      ...(displayDurationHours === undefined
        ? {}
        : { display_duration: displayDurationHours }),
    }
    return wireToAnnouncement(
      await returnData(
        apiClientVersioned.patch<IAnnouncementWire>(adminPath(id), body),
      ),
    )
  },

  remove: (id: string) =>
    returnData(apiClientVersioned.delete<void>(adminPath(id))),

  /**
   * Publish — or re-publish, which is the same thing: `is_active` is the whole
   * lifecycle, there being no revision to snapshot.
   *
   * The server re-stamps `published_at` only on a false→true *transition*,
   * deliberately, so that editing a live announcement's title does not silently
   * extend its window. But nothing flips `is_active` back when the window
   * closes, so an expired announcement is still active — and publishing it again
   * would come back untouched and stay invisible, with no Unpublish button
   * offered either (the UI reads expired as a draft). Forcing the transition is
   * what makes the button do what it says.
   *
   * Hence `isCurrentlyActive`: the withdraw-first PATCH is only sent when there
   * is a transition to force, which keeps the common case (publishing a draft) a
   * single call that cannot half-fail.
   *
   * `content` sizes the window and must be what the server already holds — the
   * caller flushes pending edits first.
   */
  publish: async (
    id: string,
    content: IAnnouncementContent,
    isCurrentlyActive = false,
  ): Promise<IAnnouncement> => {
    // Only force the transition when there is one to force. A draft is already
    // inactive, so the extra PATCH bought nothing and opened a window where a
    // failure of the second call left a live announcement withdrawn.
    if (isCurrentlyActive)
      await AnnouncementsApi.update(id, { isActive: false })

    try {
      return await AnnouncementsApi.update(id, {
        isActive: true,
        displayDurationHours: publishWindowHours(content, nowInSeconds()),
      })
    } catch (error) {
      // The withdrawal already landed, so failing here would leave an
      // announcement that *was* live invisible to everyone. Put it back: the
      // window restarts, which beats disappearing with only a toast to say so.
      if (isCurrentlyActive) {
        await AnnouncementsApi.update(id, { isActive: true }).catch(
          () => undefined,
        )
      }
      throw error
    }
  },

  unpublish: (id: string): Promise<IAnnouncement> =>
    AnnouncementsApi.update(id, { isActive: false }),
  // #endregion

  // #region End user
  /**
   * Active announcements for the current user.
   *
   * An endpoint of its own since EVGPT-1725's MR review moved it there. It used
   * to ride on an `announcements` array on `GET /users/me`, and that field is
   * gone — so reading the user payload served every user nothing, with no error
   * to say so.
   *
   * Rejects rather than answering with a sentinel when the endpoint cannot
   * answer, a 404 from an API predating it included: the caller reads a failed
   * query as "there is nobody to ask" and falls back to the built-in notices.
   * An empty array is a real answer — the admins have published nothing.
   */
  getActive: async (): Promise<IActiveAnnouncement[]> => {
    const rows = await returnData(
      apiClientVersioned.get<IUserAnnouncementWire[]>('/announcements'),
    )
    // The route serves a bare array. Anything else is a proxy or an API
    // answering in a shape this cannot read, which is no announcement to show.
    return Array.isArray(rows) ? rows.map(wireToActiveAnnouncement) : []
  },

  /**
   * Records that the user dealt with the announcement. `isDismissed` carries
   * the "Don't remind me again" tick: false still refreshes `last_saw`, which
   * is what the `repeat` cooldown counts from.
   */
  dismiss: (id: string, isDismissed: boolean) =>
    returnData(
      apiClientVersioned.patch<IAnnouncementDismissResponseWire>(
        `/announcements/${encodeURIComponent(id)}`,
        { is_dismissed: isDismissed },
      ),
    ),
  // #endregion
}
